import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { useMetaTags } from '../hooks/useMetaTags';
import { trackMetaEvent } from '../hooks/useMetaPixel';
import { useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { getPublisherParams } from '../utils/publisherParams';
import { degreeLevelOptions, areaStudyOptions, programOptions } from '../data/filterOptions';
// Value mapping helpers for Media Matchers API
const educationToApiValue = (formValue: string): number => {
  const map: Record<string, number> = {
    'some-high-school': 2,
    'high-school': 1,
    'some-college': 5,
    associates: 5,
    bachelors: 7,
    masters: 7,
    doctorate: 7
  };
  return map[formValue] || 9; // Unknown
};
const startTimeframeToApiValue = (formValue: string): number => {
  const map: Record<string, number> = {
    immediately: 1,
    '1-3-months': 2,
    '3-6-months': 3,
    '6-12-months': 4,
    '1-year-plus': 4,
    undecided: 6
  };
  return map[formValue] || 6; // Unknown
};
const militaryAffiliationToApiValue = (formValue: string): number => {
  const map: Record<string, number> = {
    none: 1,
    'active-duty': 2,
    veteran: 3,
    guard: 4,
    spouse: 5,
    dependent: 6,
    unknown: 7
  };
  return map[formValue] || 7;
};
// Map full US state names (and a few common variants) to 2-letter
// USPS abbreviations. Used to normalize the value sent to Media-Matchers
// APIs which expect "CA" rather than "California".
const STATE_ABBREVIATIONS: Record<string, string> = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  'district of columbia': 'DC',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
  'puerto rico': 'PR'
};
const toStateAbbr = (value: string): string => {
  if (!value) return '';
  const trimmed = value.trim();
  // Already a 2-letter abbreviation
  if (trimmed.length === 2) return trimmed.toUpperCase();
  const lookup = STATE_ABBREVIATIONS[trimmed.toLowerCase()];
  return lookup || trimmed;
};
// Sanitize TCPA disclaimer HTML coming back from the Media-Matchers API.
// The API sometimes embeds <a href="…"> links pointing to partner
// privacy policies / terms pages, and we want those to render as real
// clickable links (not literal "<a href=…>" text). We allow only a
// minimal whitelist of inline tags and force every <a> to open in a
// new tab with safe rel attributes.
const sanitizeTcpaHtml = (raw: string): string => {
  if (!raw) return '';
  // Use the browser's own parser to walk the DOM and strip anything
  // outside the whitelist. This is safer than regex-only sanitization
  // because it normalizes weird markup, broken tags, attribute quoting, etc.
  if (typeof document === 'undefined') return raw;
  const ALLOWED_TAGS = new Set(['A', 'BR', 'STRONG', 'B', 'EM', 'I', 'SPAN', 'P']);
  const container = document.createElement('div');
  container.innerHTML = raw;
  const walk = (node: Element) => {
    // Iterate over a static copy because we mutate during traversal.
    Array.from(node.children).forEach(child => {
      if (!ALLOWED_TAGS.has(child.tagName)) {
        // Replace disallowed element with its text content.
        const text = document.createTextNode(child.textContent || '');
        child.replaceWith(text);
        return;
      }
      // Strip all attributes except href on <a> tags.
      Array.from(child.attributes).forEach(attr => {
        if (child.tagName === 'A' && attr.name === 'href') return;
        child.removeAttribute(attr.name);
      });
      // Force safe defaults on every link.
      if (child.tagName === 'A') {
        const href = child.getAttribute('href') || '';
        // Block javascript:, data:, vbscript: URIs.
        if (/^\s*(javascript|data|vbscript):/i.test(href)) {
          child.removeAttribute('href');
        }
        child.setAttribute('target', '_blank');
        child.setAttribute('rel', 'noopener noreferrer');
        // Tailwind-ish underline styling so the link is obvious inside
        // the dense disclaimer paragraph.
        child.setAttribute('class', 'underline text-[#e8594f] hover:text-[#d64a41]');
      }
      walk(child);
    });
  };
  walk(container);
  return container.innerHTML;
};
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
};
interface FormData {
  degreeLevel: number;
  areaOfStudy: number;
  program: number;
  highestEducation: string;
  hsGraduationYear: string;
  militaryAffiliation: string;
  startDate: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  consent: boolean;
}
interface SchoolResult {
  name?: string;
  schoolName?: string;
  logoUrl?: string;
  logo?: string;
  programName?: string;
  mRecordID?: string;
  recordID?: string;
  id?: string;
  tcpaText?: string;
  tcpa?: string;
}
export function RequestInformationPage() {
  const location = useLocation();
  useMetaTags({
    title: 'Find Your Perfect Program | FindOnlineDegrees',
    description: 'Get matched with accredited online degree programs tailored to your goals.'
  });
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    degreeLevel: 0,
    areaOfStudy: 0,
    program: 0,
    highestEducation: '',
    hsGraduationYear: '',
    militaryAffiliation: '',
    startDate: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    consent: false
  });
  const [error, setError] = useState('');
  const [searchResults, setSearchResults] = useState<SchoolResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentSchoolIndex, setCurrentSchoolIndex] = useState(0);
  const [isSubmittingSchool, setIsSubmittingSchool] = useState(false);
  const [schoolTcpaChecked, setSchoolTcpaChecked] = useState(false);
  const [schoolError, setSchoolError] = useState('');
  // Clicks Search API state — drives the "additional matches" step
  // that runs after the Leads carousel. Same school-card UI as step 7
  // but no TCPA checkbox; clicking the CTA opens the partner's
  // clickURL and advances to the next match.
  const [clicksResults, setClicksResults] = useState<SchoolResult[]>([]);
  const [clicksLoading, setClicksLoading] = useState(false);
  const [clicksIndex, setClicksIndex] = useState(0);
  // TCPA Search/Post API state — drives the contact-step (step 6) consent
  // disclaimer. Disclaimer text comes from the SEARCH API response; the
  // POST API is pinged on final submit with mRecordID + contact info.
  interface TcpaSearchResult {
    tcpaRequestId?: string;
    mRecordID?: string;
    mSessionID?: string;
    mBrandName?: string;
    mBrandID?: string;
    tcpaDisclaimer?: string;
    imageURL?: string;
  }
  const [tcpaResult, setTcpaResult] = useState<TcpaSearchResult | null>(null);
  const [tcpaLoading, setTcpaLoading] = useState(false);
  const [tcpaError, setTcpaError] = useState('');
  // Stable mSessionID for the lifetime of this page-view. Initialized
  // ONCE from the URL (?mSessionID=…) if provided by the upstream
  // referrer, otherwise generated fresh. All four Media-Matchers API
  // calls (leads search/post, tcpa search/post) use the same value so
  // server-side they can be correlated to a single session.
  const mSessionIdRef = useRef<string>('');
  if (!mSessionIdRef.current) {
    const incoming = new URLSearchParams(location.search).get('mSessionID');
    mSessionIdRef.current = incoming && incoming.trim() ? incoming.trim() : `s-${generateUUID()}`;
  }
  // Pre-populate the form from URL query params when the user lands here
  // from a partner referrer (e.g. the bridge page) that has already
  // collected first name, last name, and email. We only set values that
  // are present in the URL and don't already have something in state.
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const prefill: Partial<Record<keyof FormData, string>> = {};
    const firstName = urlParams.get('firstName');
    const lastName = urlParams.get('lastName');
    const email = urlParams.get('email');
    if (firstName) prefill.firstName = firstName;
    if (lastName) prefill.lastName = lastName;
    if (email) prefill.email = email;
    if (Object.keys(prefill).length > 0) {
      console.log('[Prefill] applying URL params:', prefill);
      setFormData(prev => ({
        ...prev,
        ...prefill
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    fetchUserLocation();
  }, []);
  // If the user lands on the address step and the geo lookup never
  // populated city/state/zip (first attempt failed silently, or providers
  // were blocked at page load), try again so the address fields can be
  // pre-filled before the user has to type them.
  useEffect(() => {
    if (step !== 5) return;
    if (formData.zipCode && formData.city && formData.state) return;
    console.log('[Geo] re-attempting IP lookup on address step');
    fetchUserLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  // Inject LeadID + TrustedForm tracking scripts on mount.
  // Both scripts write their values into hidden form fields rendered below
  // (name="leadid_token" and name="xxTrustedFormCertUrl").
  useEffect(() => {
    // LeadID callback — writes the token into the leadid_token input
    ;
    (window as any).myCallbackFunction = function (token: string) {
      const field = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
      if (field) field.setAttribute('value', token);
    };
    // LeadID campaign script
    const leadIdScript = document.createElement('script');
    leadIdScript.id = 'LeadiDscript_campaign';
    leadIdScript.type = 'text/javascript';
    leadIdScript.async = true;
    leadIdScript.src = '//create.lidstatic.com/campaign/d50a3557-9854-7b8f-792c-527750401f5f.js?snippet_version=2&callback=myCallbackFunction';
    document.body.appendChild(leadIdScript);
    // TrustedForm script — writes cert URL into xxTrustedFormCertUrl input
    const tfScript = document.createElement('script');
    tfScript.type = 'text/javascript';
    tfScript.async = true;
    const provideReferrer = false;
    tfScript.src = 'http' + ('https:' === document.location.protocol ? 's' : '') + '://api.trustedform.com/trustedform.js?provide_referrer=' + escape(String(provideReferrer)) + '&field=' + escape('xxTrustedFormCertUrl') + '&l=' + new Date().getTime() + Math.random();
    document.body.appendChild(tfScript);
    return () => {
      // Best-effort cleanup so React StrictMode / route changes don't double-inject
      try {
        leadIdScript.parentNode?.removeChild(leadIdScript);
      } catch {}
      try {
        tfScript.parentNode?.removeChild(tfScript);
      } catch {}
    };
  }, []);
  // IP → ZIP lookup with multi-provider fallback. ipapi.co is the first
  // choice but it rate-limits aggressively (1k/day per IP) and returns
  // opaque 429s that surface as `Failed to fetch` in the browser. We fall
  // through to ipwho.is and ipinfo.io if earlier providers fail.
  const fetchUserLocation = async () => {
    interface GeoResult {
      city: string;
      state: string;
      zip: string;
    }
    const providers: {
      name: string;
      fetch: () => Promise<GeoResult | null>;
    }[] = [{
      name: 'ipapi.co',
      fetch: async () => {
        const res = await fetch('https://ipapi.co/json/');
        if (!res.ok) throw new Error(`ipapi.co ${res.status}`);
        const data = await res.json();
        if (data?.error) throw new Error(`ipapi.co: ${data.reason}`);
        return {
          city: data.city || '',
          state: data.region_code || '',
          zip: data.postal || ''
        };
      }
    }, {
      name: 'ipwho.is',
      fetch: async () => {
        const res = await fetch('https://ipwho.is/');
        if (!res.ok) throw new Error(`ipwho.is ${res.status}`);
        const data = await res.json();
        if (data?.success === false) throw new Error(`ipwho.is: ${data.message}`);
        return {
          city: data.city || '',
          state: data.region_code || data.region || '',
          zip: data.postal || ''
        };
      }
    }, {
      name: 'ipinfo.io',
      fetch: async () => {
        const res = await fetch('https://ipinfo.io/json');
        if (!res.ok) throw new Error(`ipinfo.io ${res.status}`);
        const data = await res.json();
        return {
          city: data.city || '',
          state: data.region || '',
          zip: data.postal || ''
        };
      }
    }];
    for (const p of providers) {
      try {
        const result = await p.fetch();
        if (result && (result.zip || result.city || result.state)) {
          console.log(`[Geo] resolved via ${p.name}`, result);
          setFormData(prev => ({
            ...prev,
            // Don't overwrite anything the user has already typed
            city: prev.city || result.city,
            state: prev.state || result.state,
            zipCode: prev.zipCode || result.zip
          }));
          return;
        }
      } catch (err) {
        console.warn(`[Geo] ${p.name} failed:`, err);
      }
    }
    console.warn('[Geo] all IP geolocation providers failed');
  };
  const lookupZipCode = async (zip: string) => {
    if (zip.length !== 5) return;
    try {
      const response = await fetch(`https://api.zippopotam.us/us/${zip}`);
      const data = await response.json();
      if (data.places && data.places[0]) {
        updateField('city', data.places[0]['place name']);
        updateField('state', data.places[0]['state abbreviation']);
      }
    } catch (err) {
      console.log('Could not lookup zip code');
    }
  };
  const updateField = (field: keyof FormData, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };
  // JSONP helper — bypasses CORS by loading the API as a script tag.
  // The API must support a callback query param (most lead APIs do).
  const jsonpRequest = <T,>(url: string, timeoutMs = 8000): Promise<T> => {
    return new Promise((resolve, reject) => {
      const callbackName = `mmcb_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
      const script = document.createElement('script');
      let settled = false;
      const cleanup = () => {
        try {
          delete (window as any)[callbackName];
        } catch {
          ;
          (window as any)[callbackName] = undefined;
        }
        if (script.parentNode) script.parentNode.removeChild(script);
      };
      (window as any)[callbackName] = (data: T) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(data);
      };
      const sep = url.includes('?') ? '&' : '?';
      script.src = `${url}${sep}callback=${callbackName}`;
      script.async = true;
      script.onerror = () => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error('JSONP request failed'));
      };
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error('JSONP request timed out'));
      }, timeoutMs);
      (window as any)[callbackName] = (origCb => (data: T) => {
        clearTimeout(timer);
        origCb(data);
      })((window as any)[callbackName]);
      document.body.appendChild(script);
    });
  };
  // The Leads Search supports an optional `excludeBrand` parameter — we
  // pass the brand id returned by the TCPA Search here so the Leads
  // result set never contains the same brand the user just consented
  // to on step 6 (avoiding a duplicate offer on step 7).
  const callSearchAPI = async (excludeBrand: string = '') => {
    setSearchLoading(true);
    const urlParams = new URLSearchParams(location.search);
    // Read values populated by the LeadID + TrustedForm scripts.
    // Fall back to a generated UUID for leadId if the script hasn't fired yet.
    const leadIdInput = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
    const tfInput = document.getElementsByName('xxTrustedFormCertUrl')[0] as HTMLInputElement | undefined;
    const leadIdToken = leadIdInput?.value || generateUUID();
    const trustedFormUrl = tfInput?.value || '';
    const searchParams = new URLSearchParams({
      degreeInterest: String(formData.degreeLevel),
      areaStudy: String(formData.areaOfStudy),
      mProgram: String(formData.program),
      highestEducation: String(educationToApiValue(formData.highestEducation)),
      gradYear: formData.hsGraduationYear === 'before-1965' ? '1965' : formData.hsGraduationYear,
      campusType: '1',
      militaryAffiliation: String(militaryAffiliationToApiValue(formData.militaryAffiliation)),
      startTimeframe: String(startTimeframeToApiValue(formData.startDate)),
      sub1: urlParams.get('sub1') || '',
      sub2: urlParams.get('sub2') || '',
      sub3: urlParams.get('sub3') || '',
      zipCode: formData.zipCode,
      address: formData.streetAddress,
      city: formData.city,
      state: toStateAbbr(formData.state),
      mediaType: '1',
      leadIdToken,
      trustedFormUrl,
      mSessionID: mSessionIdRef.current
    });
    Object.entries(getPublisherParams(location.search, {
      mPubGroupID: 'PCGP4731148320'
    })).forEach(([k, v]) => searchParams.set(k, v));
    if (excludeBrand) {
      searchParams.set('excludeBrand', excludeBrand);
    }
    const url = `https://leads.media-matchers.com/search?${searchParams.toString()}`;
    console.log('[Leads Search API] PING:', url, excludeBrand ? `(excluding brand ${excludeBrand})` : '');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, {
        signal: controller.signal
      });
      clearTimeout(timeout);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: any = await response.json();
      console.log('[Leads Search API] response:', data);
      const schools = data?.results || data?.schools || data?.data || data?.result || (Array.isArray(data) ? data : []) || [];
      // Drop "ghost" entries that don't have a usable brand name — these
      // would otherwise render as a generic "Partner School" card with
      // an "Online Degree Program" pill and a templated TCPA disclaimer.
      const validSchools = Array.isArray(schools) ? schools.filter((s: any) => s?.mBrandName || s?.brandName || s?.name || s?.schoolName) : [];
      console.log(`[Leads Search API] ${validSchools.length} valid of ${Array.isArray(schools) ? schools.length : 0} returned schools`);
      setSearchResults(validSchools);
    } catch (err) {
      clearTimeout(timeout);
      console.error('Search API error:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };
  // TCPA Search API — fires when user reaches the contact step. Result
  // populates the consent disclaimer text.
  const callTcpaSearchAPI = async () => {
    setTcpaLoading(true);
    setTcpaError('');
    setTcpaResult(null);
    const urlParams = new URLSearchParams(location.search);
    const leadIdInput = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
    const leadIdToken = leadIdInput?.value || generateUUID();
    const searchParams = new URLSearchParams({
      degreeInterest: String(formData.degreeLevel),
      areaStudy: String(formData.areaOfStudy),
      mProgram: String(formData.program),
      highestEducation: String(educationToApiValue(formData.highestEducation)),
      gradYear: formData.hsGraduationYear === 'before-1965' ? '1965' : formData.hsGraduationYear,
      campusType: '1',
      militaryAffiliation: String(militaryAffiliationToApiValue(formData.militaryAffiliation)),
      startTimeframe: String(startTimeframeToApiValue(formData.startDate)),
      sub1: urlParams.get('sub1') || '',
      sub2: urlParams.get('sub2') || '',
      sub3: urlParams.get('sub3') || '',
      zipCode: formData.zipCode,
      address: formData.streetAddress,
      city: formData.city,
      state: toStateAbbr(formData.state),
      mediaType: '2',
      leadIdToken,
      mSessionID: mSessionIdRef.current
    });
    Object.entries(getPublisherParams(location.search, {
      mPubGroupID: 'PCGP4731148320'
    })).forEach(([k, v]) => searchParams.set(k, v));
    const url = `https://tcpa.media-matchers.com/search?${searchParams.toString()}`;
    console.log('[TCPA Search API] PING (GET):', url);
    // Primary path: plain GET via fetch. If CORS blocks the response,
    // fall back to JSONP (the API must support ?callback=…). Both paths
    // are GET requests so the server-side handling is identical.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, {
        signal: controller.signal
      });
      clearTimeout(timeout);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: TcpaSearchResult = await response.json();
      console.log('[TCPA Search API] response (fetch):', data);
      setTcpaResult(data);
      setTcpaLoading(false);
      return data.mBrandID || '';
    } catch (fetchErr) {
      clearTimeout(timeout);
      console.warn('[TCPA Search API] fetch failed, falling back to JSONP:', fetchErr);
      try {
        const data = await jsonpRequest<TcpaSearchResult>(url, 8000);
        console.log('[TCPA Search API] response (jsonp):', data);
        setTcpaResult(data);
        setTcpaLoading(false);
        return data.mBrandID || '';
      } catch (jsonpErr) {
        console.error('[TCPA Search API] both fetch and JSONP failed:', jsonpErr);
        setTcpaError('Unable to load consent terms. Please refresh and try again.');
        setTcpaLoading(false);
        return '';
      }
    }
  };
  // TCPA Post API — fired on final submit with the mRecordID from the
  // TCPA search response and the user's contact info.
  //
  // Method: GET. We send the request THREE ways for maximum reliability,
  // because the user navigates to step 7 immediately after — and some
  // browsers cancel in-flight requests when the originating DOM element
  // is garbage-collected or the page re-renders:
  //   1) fetch(url, { keepalive: true, mode: 'no-cors' }) — survives
  //      navigation/unload and bypasses CORS preflight.
  //   2) <img> pixel appended to <body> — keeps the element alive long
  //      enough for the network request to complete.
  //   3) JSONP <script> tag — guaranteed visible in Network as a script
  //      request; works even if (1) and (2) are blocked.
  const callTcpaPostAPI = () => {
    const recordID = tcpaResult?.mRecordID;
    if (!recordID) {
      console.warn('[TCPA Post API] skipped — no mRecordID');
      return;
    }
    const urlParams = new URLSearchParams(location.search);
    const phoneDigits = formData.phone.replace(/\D/g, '');
    const postParams = new URLSearchParams({
      mRecordID: recordID,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: phoneDigits,
      sub1: urlParams.get('sub1') || '',
      sub2: urlParams.get('sub2') || '',
      sub3: urlParams.get('sub3') || '',
      mSessionID: mSessionIdRef.current
    });
    const url = `https://tcpa.media-matchers.com/post?${postParams.toString()}`;
    console.log('[TCPA Post API] PING (GET):', url);
    // (1) keepalive fetch — survives page navigation
    try {
      fetch(url, {
        method: 'GET',
        mode: 'no-cors',
        keepalive: true
      }).catch(err => console.warn('[TCPA Post API] fetch error:', err));
    } catch (err) {
      console.warn('[TCPA Post API] fetch threw:', err);
    }
    // (2) Image pixel — attached to body so it isn't GC'd mid-request
    try {
      const pixel = new Image();
      pixel.style.display = 'none';
      pixel.onload = () => console.log('[TCPA Post API] pixel loaded');
      pixel.onerror = () => console.warn('[TCPA Post API] pixel onerror (request may still have hit the server)');
      pixel.src = url;
      document.body.appendChild(pixel);
    } catch (err) {
      console.warn('[TCPA Post API] pixel error:', err);
    }
    // (3) JSONP fallback — guaranteed visible network request
    try {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.onload = () => {
        console.log('[TCPA Post API] jsonp loaded');
        script.parentNode?.removeChild(script);
      };
      script.onerror = () => {
        console.warn('[TCPA Post API] jsonp onerror (request still went out)');
        script.parentNode?.removeChild(script);
      };
      document.body.appendChild(script);
    } catch (err) {
      console.warn('[TCPA Post API] jsonp error:', err);
    }
  };
  // Clicks Search API — fires after the user enters the Leads carousel
  // so results are ready by the time they finish. Returns partner
  // schools with a `clickURL` field; the user is redirected to that URL
  // when they click through. Same form payload as the Leads search.
  // The Clicks Search also accepts excludeBrand so the post-Leads
  // "additional matches" carousel never re-offers the same brand the
  // user already saw on step 6 (TCPA consent).
  const callClicksSearchAPI = async (excludeBrand: string = '') => {
    setClicksLoading(true);
    setClicksResults([]);
    const urlParams = new URLSearchParams(location.search);
    const leadIdInput = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
    const leadIdToken = leadIdInput?.value || generateUUID();
    const searchParams = new URLSearchParams({
      degreeInterest: String(formData.degreeLevel),
      areaStudy: String(formData.areaOfStudy),
      mProgram: String(formData.program),
      highestEducation: String(educationToApiValue(formData.highestEducation)),
      gradYear: formData.hsGraduationYear === 'before-1965' ? '1965' : formData.hsGraduationYear,
      campusType: '1',
      militaryAffiliation: String(militaryAffiliationToApiValue(formData.militaryAffiliation)),
      startTimeframe: String(startTimeframeToApiValue(formData.startDate)),
      sub1: urlParams.get('sub1') || '',
      sub2: urlParams.get('sub2') || '',
      sub3: urlParams.get('sub3') || '',
      zipCode: formData.zipCode,
      address: formData.streetAddress,
      city: formData.city,
      state: toStateAbbr(formData.state),
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone.replace(/\D/g, ''),
      mediaType: '2',
      leadIdToken,
      mSessionID: mSessionIdRef.current
    });
    Object.entries(getPublisherParams(location.search, {
      mPubGroupID: 'PCGP4731148320'
    })).forEach(([k, v]) => searchParams.set(k, v));
    if (excludeBrand) {
      searchParams.set('excludeBrand', excludeBrand);
    }
    const url = `https://api.media-matchers.com/search?${searchParams.toString()}`;
    console.log('[Clicks Search API] PING (GET):', url, excludeBrand ? `(excluding brand ${excludeBrand})` : '');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, {
        signal: controller.signal
      });
      clearTimeout(timeout);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: any = await response.json();
      console.log('[Clicks Search API] response (fetch):', data);
      const schools = data?.results || data?.schools || data?.data || data?.result || (Array.isArray(data) ? data : []) || [];
      setClicksResults(Array.isArray(schools) ? schools : []);
    } catch (fetchErr) {
      clearTimeout(timeout);
      console.warn('[Clicks Search API] fetch failed, falling back to JSONP:', fetchErr);
      try {
        const data: any = await jsonpRequest(url, 8000);
        console.log('[Clicks Search API] response (jsonp):', data);
        const schools = data?.results || data?.schools || data?.data || data?.result || (Array.isArray(data) ? data : []) || [];
        setClicksResults(Array.isArray(schools) ? schools : []);
      } catch (jsonpErr) {
        console.error('[Clicks Search API] both fetch and JSONP failed:', jsonpErr);
        setClicksResults([]);
      }
    } finally {
      setClicksLoading(false);
    }
  };
  const callPostAPI = async (school: any) => {
    const recordID = school.mRecordID || school.recordID || school.mBrandID || school.brandID || school.id || '';
    if (!recordID) return;
    const urlParams = new URLSearchParams(location.search);
    const phoneDigits = formData.phone.replace(/\D/g, '');
    const postParams = new URLSearchParams({
      mRecordID: recordID,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: phoneDigits,
      sub1: urlParams.get('sub1') || '',
      sub2: urlParams.get('sub2') || '',
      sub3: urlParams.get('sub3') || '',
      mSessionID: mSessionIdRef.current
    });
    // Fire-and-forget via image pixel — bypasses CORS since we don't need
    // to read the response. Falls back silently on error.
    try {
      const pixel = new Image();
      pixel.src = `https://leads.media-matchers.com/post?${postParams.toString()}`;
    } catch (err) {
      console.error('Post API error:', err);
    }
  };
  const handleContinue = () => {
    if (step === 0) {
      if (!formData.degreeLevel) {
        setError('Please select a degree level');
        return;
      }
      if (!formData.areaOfStudy) {
        setError('Please select an area of study');
        return;
      }
      if (!formData.program) {
        setError('Please select a program');
        return;
      }
      setStep(1);
      return;
    }
    if (step === 1) {
      if (!formData.highestEducation) {
        setError('Please select your highest level of education');
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      if (!formData.hsGraduationYear) {
        setError('Please select when you graduated high school');
        return;
      }
      setStep(3);
      return;
    }
    if (step === 3) {
      if (!formData.startDate) {
        setError('Please select when you would like to start classes');
        return;
      }
      setStep(4);
      return;
    }
    if (step === 4) {
      if (!formData.militaryAffiliation) {
        setError('Please select your military affiliation');
        return;
      }
      setStep(5);
      return;
    }
    if (step === 5) {
      if (!formData.streetAddress.trim()) {
        setError('Please enter your street address');
        return;
      }
      if (!formData.zipCode.trim()) {
        setError('Please enter your ZIP code');
        return;
      }
      // Trigger search APIs before moving to contact info.
      // - Leads search drives the per-school TCPA flow on step 7.
      // - TCPA search returns the disclaimer text rendered on step 6.
      callTcpaSearchAPI().then(excludeBrand => {
        callSearchAPI(excludeBrand);
      });
      setStep(6);
      return;
    }
    if (step === 6) {
      if (!formData.firstName.trim()) {
        setError('Please enter your first name');
        return;
      }
      if (!formData.lastName.trim()) {
        setError('Please enter your last name');
        return;
      }
      if (!formData.email.trim()) {
        setError('Please enter your email address');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setError('Please enter a valid email address');
        return;
      }
      if (!formData.phone.trim()) {
        setError('Please enter your phone number');
        return;
      }
      if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
        setError('Please enter a valid 10-digit phone number');
        return;
      }
      if (!formData.consent) {
        setError('Please agree to be contacted');
        return;
      }
      handleSubmit();
      return;
    }
  };
  const handleBack = () => {
    if (step > 0 && step <= 6) {
      setStep(step - 1);
      setError('');
    }
  };
  // Auto-advance when single-select steps get a value
  useEffect(() => {
    if (step === 1 && formData.highestEducation) {
      const timer = setTimeout(() => {
        setError('');
        setStep(2);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.highestEducation, step]);
  useEffect(() => {
    if (step === 2 && formData.hsGraduationYear) {
      const timer = setTimeout(() => {
        setError('');
        setStep(3);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.hsGraduationYear, step]);
  useEffect(() => {
    if (step === 3 && formData.startDate) {
      const timer = setTimeout(() => {
        setError('');
        setStep(4);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.startDate, step]);
  useEffect(() => {
    if (step === 4 && formData.militaryAffiliation) {
      const timer = setTimeout(() => {
        setError('');
        setStep(5);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.militaryAffiliation, step]);
  // Format phone number as XXX-XXX-XXXX
  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.startsWith('1')) {
      return formData.phone;
    }
    const limited = digits.slice(0, 10);
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 6) {
      return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    } else {
      return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
    }
  };
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    updateField('phone', formatted);
  };
  const handleSubmit = () => {
    console.log('Form submitted:', formData);
    // Meta Pixel: CompleteRegistration — the user has checked the TCPA
    // consent box and submitted the contact form (step 6 → step 7).
    //
    // IMPORTANT: fire this FIRST and on its own, then defer everything
    // that follows (TCPA Post pixels, Clicks fetch, and the step 6 → 7
    // transition) to the next tick. The fbevents.js beacon for the
    // conversion is dispatched asynchronously; if we immediately tear
    // down the step-6 form (which unmounts the LeadID/TrustedForm
    // scripts) and fire a burst of other network requests + re-renders
    // in the SAME synchronous tick, the pixel beacon can be cancelled
    // before it leaves the browser (which is why it never showed up in
    // Pixel Helper / Test Events even though the fbq call ran). The
    // short delay gives the conversion beacon a clean window to send.
    trackMetaEvent('CompleteRegistration', {
      content_name: formData.program?.toString(),
      content_category: formData.areaOfStudy?.toString()
    });
    setTimeout(() => {
      // Fire the TCPA Post API now that the user has checked the consent
      // box and provided their contact info.
      callTcpaPostAPI();
      // Kick off the Clicks Search API in the background so the
      // "additional matches" step (8) has results ready by the time the
      // user finishes the Leads carousel. Pass the TCPA brand id as
      // excludeBrand so the Clicks carousel never re-offers the same
      // brand the user already saw on step 6.
      callClicksSearchAPI(tcpaResult?.mBrandID || '');
      // Move to step 7 (TCPA per school) instead of redirecting immediately
      setStep(7);
    }, 350);
  };
  const handleSchoolAgree = async () => {
    if (!schoolTcpaChecked) {
      setSchoolError('Please check the box to consent before continuing.');
      return;
    }
    const currentSchool = searchResults[currentSchoolIndex];
    // Meta Pixel: Search — the user clicked through a school in the carousel.
    trackMetaEvent('Search', {
      content_name: formData.program?.toString(),
      content_category: formData.areaOfStudy?.toString(),
      search_string: (currentSchool as any)?.mBrandName || (currentSchool as any)?.brandName || (currentSchool as any)?.name || ''
    });
    setIsSubmittingSchool(true);
    await callPostAPI(currentSchool);
    setIsSubmittingSchool(false);
    advanceToNextSchool();
  };
  const handleSchoolSkip = () => {
    advanceToNextSchool();
  };
  const advanceToNextSchool = () => {
    setSchoolTcpaChecked(false);
    setSchoolError('');
    if (currentSchoolIndex < searchResults.length - 1) {
      setCurrentSchoolIndex(currentSchoolIndex + 1);
    } else {
      // Leads carousel done → advance to the Clicks "additional matches"
      // step. If no Clicks results are available we'll redirect home
      // from inside the step 8 render block.
      setStep(8);
    }
  };
  // Step 8 — Clicks "additional matches" handler. Opens the partner's
  // clickURL in a new tab and advances to the next school. When the
  // list is exhausted we send the user back to the home/listings page.
  const handleClickThrough = () => {
    const school: any = clicksResults[clicksIndex];
    // Meta Pixel: Search — the user clicked through a school in the
    // "additional matches" carousel.
    trackMetaEvent('Search', {
      content_name: formData.program?.toString(),
      content_category: formData.areaOfStudy?.toString(),
      search_string: school?.mBrandName || school?.brandName || school?.name || ''
    });
    const clickURL = school?.clickURL || school?.clickUrl || school?.url || school?.link;
    if (clickURL) {
      try {
        window.open(clickURL, '_blank', 'noopener,noreferrer');
      } catch (err) {
        console.warn('[Clicks] failed to open clickURL:', err);
      }
    } else {
      console.warn('[Clicks] no clickURL found on school:', school);
    }
    if (clicksIndex < clicksResults.length - 1) {
      setClicksIndex(clicksIndex + 1);
    } else {
      const params = new URLSearchParams({
        mProgram: formData.program.toString(),
        degreeInterest: formData.degreeLevel.toString()
      });
      window.location.href = `/?${params.toString()}`;
    }
  };
  const handleClicksSkip = () => {
    if (clicksIndex < clicksResults.length - 1) {
      setClicksIndex(clicksIndex + 1);
    } else {
      const params = new URLSearchParams({
        mProgram: formData.program.toString(),
        degreeInterest: formData.degreeLevel.toString()
      });
      window.location.href = `/?${params.toString()}`;
    }
  };
  const availablePrograms = formData.areaOfStudy ? programOptions[formData.areaOfStudy] || [] : [];
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: 60
  }, (_, i) => currentYear - i);
  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 0.5rem center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '1.5em 1.5em',
    paddingRight: '2.5rem'
  };
  // Step 7: TCPA per school
  if (step === 7) {
    // If search is still loading, show spinner
    if (searchLoading) {
      return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-1421">
          <div className="text-center" data-id="element-1422">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#e8594f] mb-4" data-id="element-1423"></div>
            <p className="text-xl text-gray-700 font-semibold" data-id="element-1424">
              Finding your school matches...
            </p>
          </div>
        </div>;
    }
    // If Leads returned no usable schools, skip the Leads carousel
    // entirely and jump directly to the Clicks carousel (step 8). The
    // Clicks Search has already been fired in the background by
    // handleSubmit, so it will either be ready or still loading by the
    // time step 8 renders.
    if (!searchResults || searchResults.length === 0) {
      console.log('[Step 7] no valid Leads schools — skipping to Clicks carousel (step 8)');
      setStep(8);
      return null;
    }
    const currentSchool: any = searchResults[currentSchoolIndex];
    const schoolName = currentSchool.mBrandName || currentSchool.brandName || currentSchool.name || currentSchool.schoolName || 'Partner School';
    const schoolLogo = currentSchool.imageURL || currentSchool.imageUrl || currentSchool.logoUrl || currentSchool.logo;
    const programName = currentSchool.exactProgramName || currentSchool.matchingProgramName || currentSchool.programName || 'Online Degree Program';
    const schoolHighlight = currentSchool.schoolHighlight || '';
    const aboutText = currentSchool.aboutBrand || currentSchool.aboutCollege || currentSchool.shortDescription || '';
    const richHtml = currentSchool.bodyContent || '';
    const tcpaText = currentSchool.tcpaDisclaimer || currentSchool.tcpaText || currentSchool.tcpa || currentSchool.disclosure || `By checking the box and clicking "I Agree & Continue," you consent to be contacted by ${schoolName} and its representatives regarding educational opportunities via email, phone, or text message using automated technology. Message and data rates may apply. Your consent is not required as a condition of enrollment.`;
    return <div className="min-h-screen bg-gray-50" data-id="element-1425">
        <div className="max-w-3xl mx-auto px-4 py-12" data-id="element-1426">
          {/* Progress indicator */}
          <div className="text-center mb-8" data-id="element-1427">
            <p className="text-sm text-gray-600 font-semibold" data-id="element-1428">
              School {currentSchoolIndex + 1} of {searchResults.length}
            </p>
            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden max-w-xs mx-auto" data-id="element-1429">
              <div className="h-full bg-[#e8594f] transition-all duration-300" style={{
              width: `${(currentSchoolIndex + 1) / searchResults.length * 100}%`
            }} data-id="element-1430" />
            </div>
          </div>

          {/* School card */}
          <div className="relative bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-10" data-id="element-1431">
            {schoolHighlight && <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold border border-amber-200 shadow-sm" data-id="element-1432">
                <span aria-hidden="true" data-id="element-1433">✨</span>
                {schoolHighlight}
              </span>}
            <div className="text-center mb-8" data-id="element-1434">
              {schoolLogo && <img src={schoolLogo} alt={schoolName} className="h-28 md:h-32 mx-auto mb-6 object-contain" data-id="element-1435" />}
              {aboutText && <p className="text-base text-gray-600 leading-relaxed max-w-xl mx-auto" data-id="element-1436">
                  {aboutText}
                </p>}
              <div className="mt-4 inline-flex flex-col items-center" data-id="element-1437">
                <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-1" data-id="element-1438">
                  Your matching program
                </span>
                <span className="inline-block px-4 py-1.5 rounded-full bg-[#e8594f]/10 text-[#e8594f] font-bold text-sm" data-id="element-1439">
                  {programName}
                </span>
              </div>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-3" data-id="element-1440">
              {schoolName}
            </h2>

            {richHtml && <div className="prose prose-sm max-w-none text-gray-700 mb-6 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1" dangerouslySetInnerHTML={{
            __html: richHtml
          }} data-id="element-1441" />}

            {/* TCPA consent — required */}
            <label className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6 cursor-pointer hover:bg-blue-100 transition-colors" data-id="element-1442">
              <input type="checkbox" checked={schoolTcpaChecked} onChange={e => {
              setSchoolTcpaChecked(e.target.checked);
              if (e.target.checked) setSchoolError('');
            }} className="mt-1 w-5 h-5 text-[#e8594f] border-gray-300 rounded focus:ring-[#e8594f] flex-shrink-0" data-id="element-1443" />
              <span className="text-sm text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{
              __html: sanitizeTcpaHtml(tcpaText)
            }} data-id="element-1444" />
            </label>

            {schoolError && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-1445">
                <p className="text-red-600 text-sm" data-id="element-1446">{schoolError}</p>
              </div>}

            <div className="space-y-4" data-id="element-1447">
              <button onClick={handleSchoolAgree} disabled={isSubmittingSchool || !schoolTcpaChecked} className="w-full py-4 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg text-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1448">
                {isSubmittingSchool ? 'Submitting...' : 'I Agree & Continue'}
              </button>
              <button onClick={handleSchoolSkip} disabled={isSubmittingSchool} className="w-full py-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-lg border-2 border-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1449">
                Skip This School
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  // Step 8: Additional matches via Clicks API. Same school-card design
  // as step 7 but with NO TCPA checkbox — clicking the CTA opens the
  // partner's clickURL in a new tab and advances to the next school.
  if (step === 8) {
    if (clicksLoading) {
      return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-1450">
          <div className="text-center" data-id="element-1451">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#e8594f] mb-4" data-id="element-1452"></div>
            <p className="text-xl text-gray-700 font-semibold" data-id="element-1453">
              Finding additional matches...
            </p>
          </div>
        </div>;
    }
    if (!clicksResults || clicksResults.length === 0) {
      const params = new URLSearchParams({
        mProgram: formData.program.toString(),
        degreeInterest: formData.degreeLevel.toString()
      });
      window.location.href = `/?${params.toString()}`;
      return null;
    }
    const currentClick: any = clicksResults[clicksIndex];
    const cSchoolName = currentClick.mBrandName || currentClick.brandName || currentClick.name || currentClick.schoolName || 'Partner School';
    const cSchoolLogo = currentClick.imageURL || currentClick.imageUrl || currentClick.logoUrl || currentClick.logo;
    // headContent is the marketing headline / program promo for this
    // school (e.g. "Earn a Degree in Psychology from Keiser®.").
    // It often differs from the school name and is the best signal of
    // what the user will see when they click through.
    const cHeadline = currentClick.headContent || currentClick.exactProgramName || currentClick.matchingProgramName || currentClick.programName || '';
    const cSchoolHighlight = currentClick.schoolHighlight || '';
    const cAboutText = currentClick.aboutBrand || currentClick.aboutCollege || currentClick.shortDescription || '';
    const cAccreditation = currentClick.accreditation || '';
    const cRichHtml = currentClick.bodyContent || '';
    const cClickURL = currentClick.clickURL || currentClick.clickUrl || currentClick.url || currentClick.link;
    return <div className="min-h-screen bg-gray-50" data-id="element-1454">
        <div className="max-w-3xl mx-auto px-4 py-12" data-id="element-1455">
          {/* Progress indicator */}
          <div className="text-center mb-8" data-id="element-1456">
            <p className="text-xs uppercase tracking-widest text-[#e8594f] font-bold mb-1" data-id="element-1457">
              Additional Matches
            </p>
            <p className="text-sm text-gray-600 font-semibold" data-id="element-1458">
              School {clicksIndex + 1} of {clicksResults.length}
            </p>
            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden max-w-xs mx-auto" data-id="element-1459">
              <div className="h-full bg-[#e8594f] transition-all duration-300" style={{
              width: `${(clicksIndex + 1) / clicksResults.length * 100}%`
            }} data-id="element-1460" />
            </div>
          </div>

          {/* School card */}
          <div className="relative bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-10" data-id="element-1461">
            {cSchoolHighlight && <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold border border-amber-200 shadow-sm" data-id="element-1462">
                <span aria-hidden="true" data-id="element-1463">✨</span>
                {cSchoolHighlight}
              </span>}
            <div className="text-center mb-8" data-id="element-1464">
              {cSchoolLogo && <img src={cSchoolLogo} alt={cSchoolName} className="h-28 md:h-32 mx-auto mb-6 object-contain" data-id="element-1465" />}
              {cAboutText && <p className="text-base text-gray-600 leading-relaxed max-w-xl mx-auto" data-id="element-1466">
                  {cAboutText}
                </p>}
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2" data-id="element-1467">
              {cSchoolName}
            </h2>

            {/* Marketing headline from the Clicks API (headContent). Shown
                only when it provides additional info beyond the school
                name itself. */}
            {cHeadline && cHeadline.trim() !== cSchoolName.trim() && <p className="text-base md:text-lg text-[#e8594f] font-semibold text-center mb-3" data-id="element-1468">
                {cHeadline}
              </p>}

            {cAccreditation && <p className="text-xs text-gray-500 text-center mb-5" data-id="element-1469">
                Accredited by {cAccreditation}
              </p>}

            {cRichHtml && <div className="prose prose-sm max-w-none text-gray-700 mb-6 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1" dangerouslySetInnerHTML={{
            __html: cRichHtml
          }} data-id="element-1470" />}

            <div className="space-y-4" data-id="element-1471">
              <button onClick={handleClickThrough} disabled={!cClickURL} className="w-full py-4 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg text-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1472">
                Visit {cSchoolName} →
              </button>
              <button onClick={handleClicksSkip} className="w-full py-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-lg border-2 border-gray-300 transition-all" data-id="element-1473">
                Skip This School
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50" data-id="element-1474">
      {/* Hidden tracking fields populated by LeadID + TrustedForm scripts */}
      <input type="hidden" name="leadid_token" value="" data-id="element-1475" />
      <input type="hidden" name="xxTrustedFormCertUrl" value="" data-id="element-1476" />
      {/* Landing Page - Step 0 */}
      {step === 0 && <div className="relative" data-id="element-1477">
          {/* Hero Section */}
          <div className="max-w-7xl mx-auto px-4 py-16 md:py-24" data-id="element-1478">
            <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1479">
              {/* Left Column - Content */}
              <div className="text-center lg:text-left" data-id="element-1480">
                <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-6" data-id="element-1481">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" data-id="element-1482">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-1483" />
                  </svg>
                  Trusted by 70,000+ Students
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-1484">
                  Find Your Perfect
                  <span className="block text-[#e8594f]" data-id="element-1485">Online Degree</span>
                </h1>

                <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-1486">
                  Get matched with accredited programs tailored to your goals.
                  Compare schools, explore financial aid, and start your journey
                  today.
                </p>

                {/* Trust Signals */}
                <div className="grid grid-cols-3 gap-4 mb-8" data-id="element-1487">
                  <div className="text-center" data-id="element-1488">
                    <div className="text-3xl font-bold text-[#e8594f]" data-id="element-1489">
                      100%
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-1490">Online</div>
                  </div>
                  <div className="text-center" data-id="element-1491">
                    <div className="text-3xl font-bold text-[#e8594f]" data-id="element-1492">
                      500+
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-1493">Programs</div>
                  </div>
                  <div className="text-center" data-id="element-1494">
                    <div className="text-3xl font-bold text-[#e8594f]" data-id="element-1495">
                      Free
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-1496">Matching</div>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3 mb-8" data-id="element-1497">
                  {['Accredited institutions only', 'Financial aid guidance', 'Personalized program matches', 'No application fees'].map((feature, idx) => <div key={idx} className="flex items-center gap-3 text-gray-700" data-id="element-1498">
                      <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" data-id="element-1499">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-1500" />
                      </svg>
                      <span className="font-medium" data-id="element-1501">{feature}</span>
                    </div>)}
                </div>
              </div>

              {/* Right Column - Form */}
              <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10 border border-gray-100" data-id="element-1502">
                <div className="text-center mb-8" data-id="element-1503">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-1504">
                    Get Started in 60 Seconds
                  </h2>
                  <p className="text-gray-600" data-id="element-1505">
                    Tell us about your educational goals
                  </p>
                </div>

                <div className="space-y-5" data-id="element-1506">
                  <div data-id="element-1507">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1508">
                      Degree Level <span className="text-red-500" data-id="element-1509">*</span>
                    </label>
                    <select value={formData.degreeLevel} onChange={e => updateField('degreeLevel', Number(e.target.value))} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-1510">
                      <option value="" data-id="element-1511">-- Please Select --</option>
                      {degreeLevelOptions.map(option => <option key={option.value} value={option.value} data-id="element-1512">
                          {option.label}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-1513">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1514">
                      Area of Study <span className="text-red-500" data-id="element-1515">*</span>
                    </label>
                    <select value={formData.areaOfStudy} onChange={e => {
                  updateField('areaOfStudy', Number(e.target.value));
                  updateField('program', 0);
                }} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-1516">
                      <option value="" data-id="element-1517">-- Please Select --</option>
                      {areaStudyOptions.map(option => <option key={option.value} value={option.value} data-id="element-1518">
                          {option.label}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-1519">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1520">
                      Program <span className="text-red-500" data-id="element-1521">*</span>
                    </label>
                    <select value={formData.program} onChange={e => updateField('program', Number(e.target.value))} disabled={!formData.areaOfStudy} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-1522">
                      <option value="" data-id="element-1523">
                        {formData.areaOfStudy ? '-- Please Select --' : 'Select an area of study first'}
                      </option>
                      {availablePrograms.map(option => <option key={option.value} value={option.value} data-id="element-1524">
                          {option.label}
                        </option>)}
                    </select>
                  </div>

                  {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-1525">
                      <p className="text-red-600 text-sm" data-id="element-1526">{error}</p>
                    </div>}

                  <button onClick={handleContinue} className="w-full py-4 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg text-lg transition-all shadow-lg hover:shadow-xl" data-id="element-1527">
                    Continue to Next Step →
                  </button>

                  <p className="text-center text-xs text-gray-500 mt-4" data-id="element-1528">
                    🔒 Your information is secure and confidential
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Social Proof Section */}
          <div className="bg-white py-16 border-t border-gray-100" data-id="element-1529">
            <div className="max-w-7xl mx-auto px-4" data-id="element-1530">
              <h3 className="text-center text-2xl font-bold text-gray-900 mb-12" data-id="element-1531">
                Trusted by Students Nationwide
              </h3>
              <div className="grid md:grid-cols-3 gap-8" data-id="element-1532">
                <div className="text-center p-6" data-id="element-1533">
                  <div className="text-5xl mb-4" data-id="element-1534">⭐</div>
                  <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1535">
                    4.8/5
                  </div>
                  <p className="text-gray-600" data-id="element-1536">Average Rating</p>
                </div>
                <div className="text-center p-6" data-id="element-1537">
                  <div className="text-5xl mb-4" data-id="element-1538">🎓</div>
                  <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1539">
                    70K+
                  </div>
                  <p className="text-gray-600" data-id="element-1540">Students Matched</p>
                </div>
                <div className="text-center p-6" data-id="element-1541">
                  <div className="text-5xl mb-4" data-id="element-1542">🏆</div>
                  <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1543">
                    500+
                  </div>
                  <p className="text-gray-600" data-id="element-1544">Partner Schools</p>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Form Steps (1-6) */}
      {step > 0 && step <= 6 && <div className="max-w-4xl mx-auto px-4 py-12" data-id="element-1545">
          {/* Progress Bar */}
          <div className="mb-12" data-id="element-1546">
            <div className="flex items-center justify-between mb-4 max-w-2xl mx-auto" data-id="element-1547">
              <div className="flex-1 text-center" data-id="element-1548">
                <div className={`text-sm font-semibold mb-2 transition-colors ${step >= 1 && step < 6 ? 'text-[#e8594f]' : 'text-gray-400'}`} data-id="element-1549">
                  ABOUT YOU
                </div>
              </div>
              <div className="flex-1 text-center" data-id="element-1550">
                <div className={`text-sm font-semibold mb-2 transition-colors ${step >= 6 ? 'text-[#e8594f]' : 'text-gray-400'}`} data-id="element-1551">
                  CONTACT INFO
                </div>
              </div>
              <div className="flex-1 text-center" data-id="element-1552">
                <div className={`text-sm font-semibold mb-2 transition-colors ${step > 6 ? 'text-[#e8594f]' : 'text-gray-400'}`} data-id="element-1553">
                  YOUR SCHOOL MATCHES
                </div>
              </div>
            </div>

            {/* Progress Bar Track */}
            <div className="relative max-w-2xl mx-auto" data-id="element-1554">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-1555">
                <div className="h-full bg-[#e8594f] transition-all duration-500 ease-out" style={{
              width: `${step / 7 * 100}%`
            }} data-id="element-1556" />
              </div>

              {/* Progress Dots */}
              <div className="absolute top-1/2 -translate-y-1/2 w-full flex justify-between px-0" data-id="element-1557">
                <div className={`w-6 h-6 rounded-full border-4 transition-all ${step >= 1 ? 'bg-[#e8594f] border-[#e8594f]' : 'bg-white border-gray-300'}`} data-id="element-1558" />
                <div className={`w-6 h-6 rounded-full border-4 transition-all ${step >= 6 ? 'bg-[#e8594f] border-[#e8594f]' : 'bg-white border-gray-300'}`} data-id="element-1559" />
                <div className={`w-6 h-6 rounded-full border-4 transition-all ${step > 6 ? 'bg-[#e8594f] border-[#e8594f]' : 'bg-white border-gray-300'}`} data-id="element-1560" />
              </div>
            </div>

            <p className="text-center text-sm text-gray-500 mt-4" data-id="element-1561">
              Step {step} of 6
            </p>
          </div>

          {/* Back Button */}
          <button onClick={handleBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors" data-id="element-1562">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-1563" />
            <span className="font-medium" data-id="element-1564">Back</span>
          </button>

          {/* Form Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-12" data-id="element-1565">
            {/* Step 1: Highest Education */}
            {step === 1 && <div className="max-w-xl mx-auto" data-id="element-1566">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10" data-id="element-1567">
                  What is your highest level of education?
                </h2>
                <select value={formData.highestEducation} onChange={e => updateField('highestEducation', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-1568">
                  <option value="" data-id="element-1569">-- Please Select --</option>
                  <option value="some-high-school" data-id="element-1570">Some High School</option>
                  <option value="high-school" data-id="element-1571">High School Diploma/GED</option>
                  <option value="some-college" data-id="element-1572">Some College</option>
                  <option value="associates" data-id="element-1573">Associate's Degree</option>
                  <option value="bachelors" data-id="element-1574">Bachelor's Degree</option>
                  <option value="masters" data-id="element-1575">Master's Degree</option>
                  <option value="doctorate" data-id="element-1576">Doctorate</option>
                </select>
              </div>}

            {/* Step 2: HS Graduation Year */}
            {step === 2 && <div className="max-w-xl mx-auto" data-id="element-1577">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10" data-id="element-1578">
                  When did you graduate high school?
                </h2>
                <select value={formData.hsGraduationYear} onChange={e => updateField('hsGraduationYear', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-1579">
                  <option value="" data-id="element-1580">-- Please Select --</option>
                  {years.map(year => <option key={year} value={year} data-id="element-1581">
                      {year}
                    </option>)}
                  <option value="before-1965" data-id="element-1582">Before 1965</option>
                </select>
              </div>}

            {/* Step 3: Start Date */}
            {step === 3 && <div className="max-w-xl mx-auto" data-id="element-1583">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10" data-id="element-1584">
                  When would you like to start classes?
                </h2>
                <select value={formData.startDate} onChange={e => updateField('startDate', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-1585">
                  <option value="" data-id="element-1586">-- Please Select --</option>
                  <option value="immediately" data-id="element-1587">Immediately</option>
                  <option value="1-3-months" data-id="element-1588">1-3 months</option>
                  <option value="3-6-months" data-id="element-1589">3-6 months</option>
                  <option value="6-12-months" data-id="element-1590">6-12 months</option>
                  <option value="1-year-plus" data-id="element-1591">1+ year</option>
                  <option value="undecided" data-id="element-1592">Undecided</option>
                </select>
              </div>}

            {/* Step 4: Military Affiliation (NEW) */}
            {step === 4 && <div className="max-w-xl mx-auto" data-id="element-1593">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10" data-id="element-1594">
                  Are you affiliated with the U.S. military?
                </h2>
                <select value={formData.militaryAffiliation} onChange={e => updateField('militaryAffiliation', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-1595">
                  <option value="" data-id="element-1596">-- Please Select --</option>
                  <option value="none" data-id="element-1597">None</option>
                  <option value="active-duty" data-id="element-1598">Active Duty</option>
                  <option value="veteran" data-id="element-1599">Veteran</option>
                  <option value="guard" data-id="element-1600">Guard</option>
                  <option value="spouse" data-id="element-1601">Spouse</option>
                  <option value="dependent" data-id="element-1602">Dependent</option>
                  <option value="unknown" data-id="element-1603">Unknown</option>
                </select>
              </div>}

            {/* Step 5: Address (was step 4) */}
            {step === 5 && <div className="space-y-6 max-w-xl mx-auto" data-id="element-1604">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10" data-id="element-1605">
                  Where are you located?
                </h2>

                <div data-id="element-1606">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1607">
                    Street Address <span className="text-red-500" data-id="element-1608">*</span>
                  </label>
                  <input type="text" value={formData.streetAddress} onChange={e => updateField('streetAddress', e.target.value)} placeholder="123 Main St" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-300 transition-colors" data-id="element-1609" />
                </div>

                {formData.city && formData.state && formData.zipCode ? <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3" data-id="element-1610">
                    <span className="text-green-600 text-xl" data-id="element-1611">📍</span>
                    <div className="flex-1" data-id="element-1612">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-1613">
                        {formData.city}, {formData.state} {formData.zipCode}
                      </p>
                    </div>
                    <button type="button" onClick={() => {
              setFormData(prev => ({
                ...prev,
                city: '',
                state: '',
                zipCode: ''
              }));
            }} className="text-sm text-green-600 hover:text-green-700 font-semibold" data-id="element-1614">
                      change
                    </button>
                  </div> : <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-id="element-1615">
                    <div className="flex items-center gap-3 mb-3" data-id="element-1616">
                      <span className="text-yellow-600 text-xl" data-id="element-1617">📍</span>
                      <p className="text-sm font-semibold text-gray-700" data-id="element-1618">
                        Enter your ZIP Code
                      </p>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1619">
                      <input type="text" inputMode="numeric" value={formData.zipCode} onChange={e => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                updateField('zipCode', val);
                if (val.length === 5) {
                  lookupZipCode(val);
                }
              }} placeholder="e.g. 90210" maxLength={5} className="flex-1 px-3 py-2 border border-yellow-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 bg-white" data-id="element-1620" />
                      {formData.zipCode.length === 5 && !formData.city && <p className="text-xs text-red-500" data-id="element-1621">
                          ZIP not found. Try again.
                        </p>}
                    </div>
                  </div>}
              </div>}

            {/* Step 6: Contact Info (was step 5) */}
            {step === 6 && <div className="space-y-6 max-w-xl mx-auto" data-id="element-1622">
                <div className="text-center mb-8" data-id="element-1623">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2" data-id="element-1624">
                    Last step! Where should we send your matches?
                  </h2>
                  <p className="text-gray-600" data-id="element-1625">
                    We'll match you with top programs in {formData.city},{' '}
                    {formData.state}.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-1626">
                  <div data-id="element-1627">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1628">
                      First Name <span className="text-red-500" data-id="element-1629">*</span>
                    </label>
                    <input type="text" value={formData.firstName} onChange={e => updateField('firstName', e.target.value)} placeholder="John" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] hover:border-gray-300 transition-colors" data-id="element-1630" />
                  </div>
                  <div data-id="element-1631">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1632">
                      Last Name <span className="text-red-500" data-id="element-1633">*</span>
                    </label>
                    <input type="text" value={formData.lastName} onChange={e => updateField('lastName', e.target.value)} placeholder="Doe" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] hover:border-gray-300 transition-colors" data-id="element-1634" />
                  </div>
                </div>

                <div data-id="element-1635">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1636">
                    Email Address <span className="text-red-500" data-id="element-1637">*</span>
                  </label>
                  <input type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} placeholder="john@example.com" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] hover:border-gray-300 transition-colors" data-id="element-1638" />
                </div>

                <div data-id="element-1639">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1640">
                    Phone Number <span className="text-red-500" data-id="element-1641">*</span>
                  </label>
                  <input type="tel" value={formData.phone} onChange={handlePhoneChange} placeholder="555-123-4567" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] hover:border-gray-300 transition-colors" data-id="element-1642" />
                </div>

                <div className="pt-4" data-id="element-1643">
                  {tcpaLoading ? <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-1644">
                      <div className="w-5 h-5 border-2 border-gray-200 border-t-[#e8594f] rounded-full animate-spin" aria-label="Loading consent terms" data-id="element-1645" />
                      <span data-id="element-1646">Loading consent terms…</span>
                    </div> : tcpaError ? <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-id="element-1647">
                      {tcpaError}
                    </div> : tcpaResult?.tcpaDisclaimer ? <label className="flex items-start gap-3 cursor-pointer" data-id="element-1648">
                      <input type="checkbox" checked={formData.consent} onChange={e => updateField('consent', e.target.checked)} className="mt-1 w-5 h-5 text-[#e8594f] border-gray-300 rounded focus:ring-[#e8594f]" data-id="element-1649" />
                      <span className="text-xs text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{
                __html: sanitizeTcpaHtml(tcpaResult.tcpaDisclaimer)
              }} data-id="element-1650" />
                    </label> : null}
                </div>
              </div>}

            {error && <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-lg max-w-xl mx-auto" data-id="element-1651">
                <p className="text-red-600 text-sm" data-id="element-1652">{error}</p>
              </div>}

            <div className="mt-8 max-w-xl mx-auto" data-id="element-1653">
              <button onClick={handleContinue} className="w-full py-4 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg text-lg transition-all shadow-lg hover:shadow-xl" data-id="element-1654">
                {step === 6 ? 'See Matching Colleges 🎓' : 'Continue →'}
              </button>

              {step === 6 && <p className="text-center text-gray-700 mt-4" style={{
            fontSize: '12.5px',
            lineHeight: '1.5'
          }} data-id="element-1655">
                  By clicking "See Matching Colleges," you expressly consent to
                  receive autodialed and/or pre-recorded marketing text messages
                  (SMS/MMS) from FindOnlineDegrees.org at the number provided.
                  Consent is not a condition of any purchase. Msg &amp; data
                  rates may apply. Reply STOP to opt out. View our{' '}
                  <a href="/privacy-policy" className="underline text-gray-700 hover:text-gray-900" data-id="element-1656">
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a href="/terms" className="underline text-gray-700 hover:text-gray-900" data-id="element-1657">
                    Terms &amp; Conditions
                  </a>
                  .
                </p>}

              {step !== 6 && <div className="mt-5 flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800" data-id="element-1658">
                  <span className="text-blue-500 text-lg leading-none mt-0.5" data-id="element-1659">
                    ℹ️
                  </span>
                  <span data-id="element-1660">
                    {step === 0 && 'Selecting your degree level and area of study helps us match you with the most relevant accredited programs.'}
                    {step === 1 && 'Your education background helps schools understand where you are in your academic journey and recommend the right program level.'}
                    {step === 2 && 'Knowing when you graduated helps us verify eligibility and connect you with programs that fit your timeline.'}
                    {step === 3 && 'Your preferred start date helps us find programs with upcoming enrollment windows that work for your schedule.'}
                    {step === 4 && 'Military affiliation may qualify you for special benefits, scholarships, and program options designed for service members and their families.'}
                    {step === 5 && 'Your location helps us find programs available in your area and determine eligibility for state-specific financial aid.'}
                  </span>
                </div>}
            </div>
          </div>
        </div>}
    </div>;
}